import { Button, Input, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, Column, Row } from '@hero/components/atoms';
import { DetailItem, OrderStatusBadge } from '@hero/components/molecules';
import { ROLES, RoleSelect } from '@hero/components/organisms';
import { MAX_EMAIL_LENGTH, MAX_FIRST_NAME_LENGTH, MAX_LAST_NAME_LENGTH } from '@hero/constants';
import { useIsAutomationEnabled } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetAutomations, useGetRecognitions } from '@hero/services';
import {
  useGetOrganizationUser,
  useUpdateOrganizationUser
} from '@hero/services/organization-service/hooks';

import * as S from './style';

const CampaignListItem = ({ name }) => (
  <S.AppreciationItem>
    <Typography variant="body2" lineClamp={1}>
      {name}
    </Typography>
  </S.AppreciationItem>
);

export default function UserDetailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId: userCode } = useParams();
  const isAutomationEnabled = useIsAutomationEnabled();

  const [role, setRole] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [email, setEmail] = useState(null);

  const { data: user } = useGetOrganizationUser({ userCode }, { enabled: !!userCode });
  const { mutate: updateOrganizationUser } = useUpdateOrganizationUser();

  const { data: recognitions } = useGetRecognitions(
    {
      filters: {
        createdBy: user?.email
      },
      pagination: {
        limit: 3
      }
    },
    { enabled: !!user?.email }
  );

  const { data: automations } = useGetAutomations(
    {
      filters: {
        createdBy: user?.email
      },
      pagination: {
        limit: 3
      }
    },
    { enabled: !!user?.email }
  );

  const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`;

  const onRoleChange = e => {
    setRole(e);
  };

  function LastNameEditInput() {
    return (
      <Input
        value={lastName}
        inputProps={{
          maxLength: MAX_LAST_NAME_LENGTH,
          onChange: e => setLastName(e.target.value)
        }}
        formItem={{
          placeholder: t('enterASenderName'),
          label: t('lastName'),
          name: 'lastName',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('lastName') })
            }
          }
        }}
      />
    );
  }

  function FirstNameEditInput() {
    return (
      <Input
        value={firstName}
        inputProps={{
          maxLength: MAX_FIRST_NAME_LENGTH,
          onChange: e => setFirstName(e.target.value)
        }}
        formItem={{
          placeholder: t('enterASenderName'),
          label: t('firstName'),
          name: 'firstName',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('firstName') })
            }
          }
        }}
      />
    );
  }

  function RoleEditInput() {
    return <RoleSelect value={role} onChange={onRoleChange} menuWidth={300} />;
  }

  useEffect(() => {
    setFirstName(user?.firstName);
    setLastName(user?.lastName);
    setEmail(user?.email);
    setRole(ROLES.find(role => role.value === user?.role));
  }, [user]);

  return (
    <S.UserDetailWrapper>
      <S.UserDetail>
        <Card>
          <S.InfoHeaderWrapper>
            <Column>
              <Row>
                <Typography variant="body2Bold">
                  {trim(userFullName) ? userFullName : '-'}
                </Typography>
                {/* <VerticalDivider $height={21} />
                <Button
                  variant="link"
                  color="error"
                  startIcon={<Icon name="delete" />}
                  onClick={() => {}}
                >
                  {t('removeUser')}
                </Button> */}
              </Row>
            </Column>
            <OrderStatusBadge status={user?.status} />
          </S.InfoHeaderWrapper>
          <S.FormContent>
            <DetailItem
              title={`${t('firstName')}:`}
              withBorder
              bodyStyle={{
                display: 'flex',
                alignItems: 'center'
              }}
              inputProps={{
                maxLength: MAX_FIRST_NAME_LENGTH
              }}
              editable
              closeWithoutSave={() => setFirstName(user?.firstName)}
              saveDetail={() => updateOrganizationUser({ userCode, body: { firstName } })}
              editInput={FirstNameEditInput}
            >
              {firstName}
            </DetailItem>
            <DetailItem
              title={`${t('lastName')}:`}
              withBorder
              bodyStyle={{
                display: 'flex',
                alignItems: 'center'
              }}
              inputProps={{
                maxLength: MAX_LAST_NAME_LENGTH
              }}
              editable
              closeWithoutSave={() => setLastName(user?.lastName)}
              saveDetail={() => updateOrganizationUser({ userCode, body: { lastName } })}
              editInput={LastNameEditInput}
            >
              {lastName}
            </DetailItem>
            <DetailItem
              title={`${t('email')}:`}
              withBorder
              inputProps={{
                maxLength: MAX_EMAIL_LENGTH
              }}
              bodyStyle={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {email}
            </DetailItem>
            <DetailItem
              title={`${t('role')}:`}
              bodyStyle={{
                display: 'flex',
                alignItems: 'center'
              }}
              editable
              closeWithoutSave={() => setRole(ROLES.find(role => role.value === user?.role))}
              saveDetail={() => updateOrganizationUser({ userCode, body: { role: role.value } })}
              editInput={RoleEditInput}
            >
              {role?.label}
            </DetailItem>
          </S.FormContent>
        </Card>
      </S.UserDetail>
      <S.AppreciationsWrapper>
        <S.AppreciationDetail>
          <Card>
            <S.FeatureBox>
              <S.HeaderGroup style={{ marginBottom: 0 }}>
                <Typography variant="body1Bold">{t('recognitions')}</Typography>
                <VerticalDivider $height={21} />
                <Button
                  variant="link"
                  onClick={() => {
                    navigate(ROUTE_PATHS.MY_RECOGNITIONS, {
                      state: { createdBy: user?.email }
                    });
                  }}
                >
                  {t('seeAll')}
                </Button>
              </S.HeaderGroup>
              <Typography
                style={{ marginBottom: 16 }}
                color="fg"
                emphasis="weaker"
                variant="caption1"
              >
                {t('recentRecognitionSendTo', { firstName: user?.firstName || `{fistName}` })}
              </Typography>
              <S.FeatureBody>
                {recognitions?.docs?.length > 0 ? (
                  recognitions.docs.map(recognition => (
                    <CampaignListItem key={recognition.code} name={recognition?.name} />
                  ))
                ) : (
                  <Typography variant="body2">
                    {t('neverBeenSendRecognition', {
                      firstName: user?.firstName || `{firstName}`
                    })}
                  </Typography>
                )}
              </S.FeatureBody>
            </S.FeatureBox>
          </Card>
        </S.AppreciationDetail>
        {isAutomationEnabled && (
          <S.AppreciationDetail>
            <Card>
              <S.FeatureBox>
                <S.HeaderGroup style={{ marginBottom: 0 }}>
                  <Typography variant="body1Bold">{t('automations')}</Typography>
                  <VerticalDivider $height={21} />
                  <Button
                    variant="link"
                    onClick={() => {
                      navigate(ROUTE_PATHS.MY_AUTOMATION_LIST, {
                        state: { createdBy: user?.email }
                      });
                    }}
                  >
                    {t('seeAll')}
                  </Button>
                </S.HeaderGroup>
                <Typography
                  color="fg"
                  emphasis="weaker"
                  style={{ marginBottom: 16 }}
                  variant="caption1"
                >
                  {t('activeAutomationIsPartOf', { firstName: user?.firstName || `{firstName}` })}
                </Typography>
                <S.FeatureBody>
                  {automations?.docs?.length > 0 ? (
                    automations.docs.map(automation => (
                      <CampaignListItem key={automation.code} name={automation?.name} />
                    ))
                  ) : (
                    <Typography variant="body2">
                      {t('notPartOfActiveAutomation', {
                        firstName: user?.firstName || `{firstName}`
                      })}
                    </Typography>
                  )}
                </S.FeatureBody>
              </S.FeatureBox>
            </Card>
          </S.AppreciationDetail>
        )}
      </S.AppreciationsWrapper>
    </S.UserDetailWrapper>
  );
}

CampaignListItem.propTypes = {
  name: PropTypes.string
};
